import { FreightStatus } from '~/src/components/shared/features/types'
import { ExceptionTypename } from '~/src/components/shared/features/hooks/types'

import colors from '~/config/colorsScheme'

import {
  LegStatus,
  LoadStatus,
  LoadTenderStatus,
  DocumentType,
  TrackingStatus,
  ShipmentStatus,
  StopLatenessStatus,
  TrailerType,
} from '~/__generated_types__/globalTypes'

export const STATUS_COLORS = {
  GREEN: 'bg-green-50 text-green-700',
  YELLOW: 'bg-yellow-50 text-yellow-900',
  RED: 'bg-red-50 text-red-700',
  GRAY: 'bg-gray-100',
  BLUE: 'bg-blue-100 text-blue-800',
}

export const STATUS_TO_COLOR_MAP = new Map<
  LoadStatus | LegStatus | TrackingStatus | ShipmentStatus,
  string
>([
  [LoadStatus.Started, STATUS_COLORS.GREEN],
  [LoadStatus.Dispatched, STATUS_COLORS.GREEN],
  [LoadStatus.Planned, STATUS_COLORS.YELLOW],
  [LoadStatus.Imported, STATUS_COLORS.BLUE],
  [LoadStatus.Suspended, STATUS_COLORS.RED],
  [LoadStatus.Completed, STATUS_COLORS.BLUE],
  [LoadStatus.Available, STATUS_COLORS.YELLOW],
  [LoadStatus.Cancelled, STATUS_COLORS.GRAY],
  [LoadStatus.InAudit, STATUS_COLORS.BLUE],
  [TrackingStatus.EnRoute, STATUS_COLORS.GREEN],
  [TrackingStatus.Completed, STATUS_COLORS.BLUE],
  [TrackingStatus.Planned, STATUS_COLORS.YELLOW],
  [TrackingStatus.Unavailable, STATUS_COLORS.GRAY],
  [ShipmentStatus.Open, STATUS_COLORS.YELLOW],
  [ShipmentStatus.OnHold, STATUS_COLORS.YELLOW],
  [ShipmentStatus.Planned, STATUS_COLORS.YELLOW],
  [ShipmentStatus.PickedUp, STATUS_COLORS.GREEN],
  [ShipmentStatus.Delivered, STATUS_COLORS.BLUE],
  [ShipmentStatus.Cancelled, STATUS_COLORS.GRAY],
  [ShipmentStatus.Unknown, STATUS_COLORS.GRAY],
])

export const FILTER_DOCUMENT_TYPES: Array<DocumentType> = [
  DocumentType.ProofOfDelivery,
  DocumentType.BillOfLading,
  DocumentType.Tender,
  DocumentType.Other,
]

export const ONE_DAY_IN_MINUTES = 24 * 60
export const UPLOADED_SORT_PREFIX = 'UPLOADED'

// TODO add support for specifying background, border and text tailwind classes
// in this constant - https://app.asana.com/0/1206956035407847/1207707746630599/f
export const LEG_COLORS = [
  {
    twColor: 'blue-500',
    hexColor: colors.blue[500],
  },
  {
    twColor: 'darkPink-500',
    hexColor: colors.darkPink[500],
  },
  {
    twColor: 'bloodOrange-500',
    hexColor: colors.bloodOrange[500],
  },
  {
    twColor: 'green-500',
    hexColor: colors.green[500],
  },
  {
    twColor: 'brightPurple-500',
    hexColor: colors.brightPurple[500],
  },
  {
    twColor: 'brightPink-500',
    hexColor: colors.brightPink[500],
  },
  {
    twColor: 'lightBlue-500',
    hexColor: colors.lightBlue[500],
  },
  {
    twColor: 'mediumPurple-500',
    hexColor: colors.mediumPurple[500],
  },
  {
    twColor: 'lime-500',
    hexColor: colors.lime[500],
  },
  {
    twColor: 'darkBlue-500',
    hexColor: colors.darkBlue[500],
  },
  {
    twColor: 'orange-500',
    hexColor: colors.orange[500],
  },
  {
    twColor: 'teal-500',
    hexColor: colors.teal[500],
  },
]

export const LEG_COLORS_HOVER = [
  {
    twColor: 'bg-blue-700',
    hexColor: colors.blue[700],
  },
  {
    twColor: 'bg-darkPink-700',
    hexColor: colors.darkPink[700],
  },
  {
    twColor: 'bg-bloodOrange-700',
    hexColor: colors.bloodOrange[700],
  },
  {
    twColor: 'bg-green-700',
    hexColor: colors.green[700],
  },
  {
    twColor: 'bg-brightPurple-700',
    hexColor: colors.brightPurple[700],
  },
  {
    twColor: 'bg-brightPink-700',
    hexColor: colors.brightPink[700],
  },
  {
    twColor: 'bg-lightBlue-700',
    hexColor: colors.lightBlue[700],
  },
  {
    twColor: 'bg-mediumPurple-700',
    hexColor: colors.mediumPurple[700],
  },
  {
    twColor: 'bg-lime-700',
    hexColor: colors.lime[700],
  },
  {
    twColor: 'bg-darkBlue-700',
    hexColor: colors.darkBlue[700],
  },
  {
    twColor: 'bg-orange-700',
    hexColor: colors.orange[700],
  },
  {
    twColor: 'bg-teal-700',
    hexColor: colors.teal[700],
  },
]

export const TOOLTIPS_OPERATORS: Record<string, string> = {
  inout: 'Arrival and departure times, in your time zone',
  transit:
    'Transit: Travel time to the location (hrs:min) Dwell: Time spent at the location (hrs:min)',
  app: 'Appointments tell the driver when they are expected to arrive at the delivery or pickup location',
  criticalApp: 'Missing this appointment can result in penalties',
}

export const INTERVAL_TIME = 1000 * 60 * 5 // every 5 mins (milliseconds)
export const TWENTY_FOUR_HOURS_IN_MILLISECONDS = 24 * 60 * 60 * 1000
export const TWENTY_FOUR_HOURS_IN_SECONDS = 24 * 60 * 60

export const DEFAULT_TIME_ZONE_NAME = 'America/New_York'
export const DEFAULT_TIME_ZONE_CODE = 'ET'

export const MINUTE_MS = 60 * 1000

export const MAX_NUMBER_COST_CENTERS = 15

export const NOT_SHARABLE_LOAD_STATUS = new Set([LoadStatus.Cancelled])

export const NOT_SHARABLE_LEG_STATUS = new Set([LegStatus.Unknown])

export const UNREAD_NOTIFICATION_COUNT_THRESHOLD = 99

export const MILLISECONDS_TO_HOURS = 1000 * 60 * 60

export const OPERATIONAL_STOP = 'Operational Stop'

export const LATENESS_STATUS_COLORS = {
  [StopLatenessStatus.RunningLate]: 'bg-yellow-600',
  [StopLatenessStatus.Late]: 'bg-red-600',
}

export const LATENESS_BG_COLORS: Map<StopLatenessStatus, string> = new Map([
  [StopLatenessStatus.Late, 'bg-red-50 rounded-sm px-0.5 w-fit'],
  [StopLatenessStatus.RunningLate, 'bg-yellow-50 rounded-sm w-fit px-0.5'],
])

// Trailer Types for List View search
export const TRAILER_TYPE: Map<TrailerType, string> = new Map([
  [TrailerType.Flatbed, 'Flatbed'],
  [TrailerType.Tanker, 'Tanker'],
  [TrailerType.Reefer, 'Reefer'],
  [TrailerType.Dry, 'Dry Van'],
  [TrailerType.Unknown, 'Unknown'],
])

export const TRACTOR_TYPE_TO_NAME: Map<string, string> = new Map([
  ['TA Tractor', 'Tandem axle'],
  ['T/A Tractor', 'Tandem axle'],
  ['TA Conv Sleeper Trct', 'Tandem axle sleeper'],
  ['T/A Conv Sleeper Trct', 'Tandem axle sleeper'],
  ['SA Tractor', 'Single axle'],
  ['S/A Tractor', 'Single axle'],
])

export const FREIGHT_STATUS_COLORS = new Map<FreightStatus, string>([
  [FreightStatus.PICKED_UP, 'bg-green-50 text-green-800'],
  [FreightStatus.OVER, 'bg-red-50 text-red-800'],
  [FreightStatus.SHORT, 'bg-red-50 text-red-800'],
  [FreightStatus.DAMAGED, 'bg-red-50 text-red-800'],
  [FreightStatus.CANCELLED, 'bg-gray-100 text-default],'],
  [FreightStatus.DELIVERED, 'bg-blue-50 text-blue-800'],
  [FreightStatus.PLANNED, 'bg-yellow-50 text-yellow-900'],
])

export const TENDER_STATUS_TO_COLOR_MAP = new Map<LoadTenderStatus, string>([
  [LoadTenderStatus.NotTendered, STATUS_COLORS.RED],
  [LoadTenderStatus.Tendered, STATUS_COLORS.GREEN],
  [LoadTenderStatus.Withdrawn, STATUS_COLORS.RED],
])

export const ORDER_STATUSES: Array<LoadStatus> = [
  LoadStatus.Planned,
  LoadStatus.Dispatched,
  LoadStatus.Started,
  LoadStatus.Completed,
  LoadStatus.Cancelled,
]

export const SEARCH_STRING_MIN_LENGTH = 3
export const VIOLATION_EXCEPTION_TYPES: Array<Partial<ExceptionTypename>> = [
  'LateArrivalException',
  'AttemptedStopException',
  'SkippedStopException',
]
